import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {BuyBonusPopUpEvents} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusPopUp/BuyBonusPopUpEvents';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels/ReelsEvents';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {AutoplayEvents} from '@gongxigames/framework/slot/modules/autoplay/AutoplayEvents';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow/FlowModuleEvents';
import {UIDisableReason} from '@gongxigames/framework/slot/modules/keypad/UIDisableReason';
import {BuyBonusButtonView} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusButton/BuyBonusButtonView';
import {BuyBonusButtonViewMobile} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusButton/BuyBonusButtonViewMobile';
import {BuyBonusButtonBasicState} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusButton/states/BuyBonusButtonBasicState';
import {BuyBonusButtonFeatureState} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusButton/states/BuyBonusButtonFeatureState';
import {BuyBonusButtonAutoPlayState} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusButton/states/BuyBonusButtonAutoplayState';
import {CoinFlightEvents} from '@gongxigames/framework/slot/modules/buyBonus/coinFlight/CoinFlightEvents';
import {BuyBonusButtonEvents} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusButton/BuyBonusButtonEvents';
import {isEnabledRubyCoins} from '@gongxigames/framework/slot/modules/buyBonus/isEnabledRubyCoins';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {ModuleIsEnabledFilter} from '@gongxigames/framework/core';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {KeypadControlsEvents} from '@gongxigames/framework/slot/modules/keypadControls/KeypadControlsEvents';

export class BuyBonusButtonModule extends Module<BuyBonusButtonView, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('BuyButtonModule');
    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledRubyCoins;

    public static readonly BASIC_STATE: string = 'basic';
    public static readonly FEATURE_STATE: string = 'feature';
    public static readonly AUTO_PLAY_STATE: string = 'autoplay';

    protected _disableReasons: any[] = [];

    public restore(): void {
        if (this.logic.nextAction !== Action.SPIN) {
            this.gotoState(BuyBonusButtonModule.FEATURE_STATE);
        }
    }

    protected setupViews(): void {
        if (this.isPlatformDesktop()) {
            this.setView(BuyBonusButtonView.NAME);
        } else {
            this.setView(BuyBonusButtonViewMobile.NAME);
        }
    }

    protected setupStates(): void {
        this.addState(new BuyBonusButtonBasicState(this, BuyBonusButtonModule.BASIC_STATE));
        this.addState(new BuyBonusButtonFeatureState(this, BuyBonusButtonModule.FEATURE_STATE));
        this.addState(new BuyBonusButtonAutoPlayState(this, BuyBonusButtonModule.AUTO_PLAY_STATE));

        this.gotoState(BuyBonusButtonModule.BASIC_STATE);
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.onCurrentStateHandle(LogicEvents.SPIN_RESPONSE);
        this.onCurrentStateHandle(BuyBonusPopUpEvents.BUY_BUTTON_CLICKED);
        this.onCurrentStateHandle(BuyBonusPopUpEvents.BUY_POP_UP_CLOSED);
        this.onCurrentStateHandle(AutoplayEvents.START_AUTOPLAY);
        this.onCurrentStateHandle(AutoplayEvents.STOP_AUTOPLAY);
        this.onCurrentStateHandle(FlowModuleEvents.ENTER_BASIC_STATE);
        this.onCurrentStateHandle(ReelsEvents.REELS_SPINNING_STARTED);
        this.onCurrentStateHandle(CoinFlightEvents.COIN_FLIGHT_FINISHED);
        this.onCurrentStateHandle(KeypadControlsEvents.TOTAL_BET_CHANGED);
        this.onCurrentStateHandle('General.UI.BALANCE.UPDATED');

        this.on('General.UI.ENABLE', this.enableButtonListener);
        this.on('General.UI.DISABLE', this.disableButtonListener);

        this.on(ReelsEvents.SPIN_ENDED, this.onAllReelsStopped);

        this.onView(BuyBonusButtonView.BUTTON_CLICKED, this.onBuyButtonClicked);
    }

    protected onAllReelsStopped(): void {
        this.enableByName(UIDisableReason.SPINNING);
    }

    protected enableButtonListener(eventID: symbol | string, reason: symbol): void {
        this.enableByName(reason);
    }

    protected disableButtonListener(eventID: symbol | string, reason: symbol): void {
        this.disableByName(reason);
    }

    protected onBuyButtonClicked(): void {
        this.eventManager.emit(BuyBonusButtonEvents.BUY_BONUS_BUTTON_CLICKED);

        this.gotoState(BuyBonusButtonModule.FEATURE_STATE);
    }

    public enableByName(reason: symbol): void {
        const disabledBy = this._disableReasons;
        const index = disabledBy.indexOf(reason);

        if (index !== -1) {
            disabledBy.splice(index, 1);

            if (!disabledBy.length && this.currentState.name === 'basic') {
                this.view.enableButton();
            }
        }
    }

    public disableByName(reason: symbol): void {
        const disabledBy = this._disableReasons;

        if (disabledBy.indexOf(reason) === -1) {
            disabledBy.push(reason);

            this.view.disableButton();
        }
    }

    public get disableReasons(): any[] {
        return this._disableReasons;
    }
}
